/**
 * LeadSignalPills — Tag-pill row for mobile lead-card (ContactDetail).
 *
 * Renders up to 4 signal pills in a single-line, horizontally scrollable row:
 *   Intent → Timeline → Trade → Finance
 *
 * DESIGN SPEC (Quill sign-off, v1):
 *  - intent_score: render "Intent {score}" only when NOT null.
 *    IMPORTANT: 0 is a real value → renders "Intent 0". null → suppress.
 *  - purchase_timeline: render raw value ("now","7d","30d","90d","research")
 *    only when not null. null → suppress.
 *  - has_trade_in: render "Trade: Y" only when true.
 *    false → suppress entirely (product decision: no noise for absent trade-in).
 *  - finance_preference: render raw value ("cash","finance","lease")
 *    only when not null. null → suppress.
 *  - Zero-pill state: render nothing at all (no placeholders, no copy).
 *  - No color-coding in v1 — all pills same neutral dark-UI style.
 *  - Not interactive (no tap handlers, no tooltip).
 *
 * Dark palette: consistent with MobileTopNav (#0b1220) and ContactDetail
 * card background (#141b2f / #2c354e border). Pill bg uses rgba(49,126,235,0.13)
 * (the brand-blue at low opacity) against #141b2f dark card background.
 * Contrast check: text #93c5fd (~#93c5fd) on #141b2f → ~5.1:1, passes WCAG AA.
 *
 * Props:
 *   intent_score       number | null
 *   purchase_timeline  string | null
 *   has_trade_in       boolean  (never null per API spec, defaults false)
 *   finance_preference string | null
 */

import React from 'react';

export default function LeadSignalPills({
  intent_score,
  purchase_timeline,
  has_trade_in,
  finance_preference,
}) {
  // Build the ordered list of pills to render.
  // Spec order: Intent → Timeline → Trade → Finance
  const pills = [];

  // 1. Intent: show ONLY when not null (0 is a real value, must render)
  if (intent_score !== null && intent_score !== undefined) {
    pills.push({ key: 'intent', label: `Intent ${intent_score}` });
  }

  // 2. Timeline: show only when not null
  if (purchase_timeline !== null && purchase_timeline !== undefined) {
    pills.push({ key: 'timeline', label: purchase_timeline });
  }

  // 3. Trade: show ONLY when true — false is suppressed by product decision
  if (has_trade_in === true) {
    pills.push({ key: 'trade', label: 'Trade: Y' });
  }

  // 4. Finance: show only when not null
  if (finance_preference !== null && finance_preference !== undefined) {
    pills.push({ key: 'finance', label: finance_preference });
  }

  // Zero-pill state: render nothing visible
  if (pills.length === 0) {
    return null;
  }

  return (
    <div style={styles.row} aria-label="Lead signals">
      {pills.map(({ key, label }) => (
        <span key={key} style={styles.pill}>
          {label}
        </span>
      ))}
    </div>
  );
}

// ─── Styles ────────────────────────────────────────────────────────────────────
// Dark palette sourced from existing components:
//   Card bg:   #141b2f  (ContactDetail S.modal)
//   Border:    #2c354e  (ContactDetail S.modal border, MobileTopNav borderBottom)
//   Brand:     var(--brand-primary, #317eeb)  (MobileTopNav wordmarkAccent, tabActive borderBottomColor)
//   Text base: #f1f5f9  (MobileTopNav wordmarkText)
//   Muted:     #94a3b8  (ContactDetail S.contactItem)
//
// Pill style:
//   bg:   rgba(49,126,235,0.13)  — brand blue at ~8% opacity → non-intrusive
//   border: rgba(49,126,235,0.32) — slightly stronger for definition
//   text: #93c5fd               — sky-blue-300, contrast vs #141b2f ≈ 5.1:1 (WCAG AA ✓)
//
// Row: single line, horizontal scroll, no wrap (spec §6).
const styles = {
  row: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    overflowX: 'auto',
    gap: '6px',
    padding: '8px 20px',
    borderBottom: '1px solid #2c354e',
    // Hide scrollbar but keep scrollability (cross-browser)
    scrollbarWidth: 'none',       // Firefox
    msOverflowStyle: 'none',      // IE/Edge
    WebkitOverflowScrolling: 'touch',
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
    padding: '4px 10px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.03em',
    border: '1px solid rgba(49, 126, 235, 0.32)',
    background: 'rgba(49, 126, 235, 0.13)',
    color: '#93c5fd',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    // Static — not tappable (per spec §10)
    pointerEvents: 'none',
  },
};
